import PropTypes from 'prop-types'
import React from 'react'
import path from 'path'
import { Router, navigate } from '@reach/router'
import { connect } from 'react-redux'

import * as actions from './actions'
import CanvasUrl from './components/CanvasUrl'
import Authorization from './components/Authorization'
import CanvasCourse from './components/CanvasCourse'
import CanvasExternalTools from './components/CanvasExternalTools'
import CanvasAssessments from './components/CanvasAssessments'
import Finished from './components/Finished'
import { Alert, StepWizard } from '../components'

const canvasUrlPath = 'url'
const authorizationPath = 'authorize'
const canvasCoursePath = 'course'
const externalToolsPath = 'tools'
const assessmentsPath = 'assessments'
const finishedPath = 'finished'

const stepPaths = [
  canvasUrlPath,
  authorizationPath,
  canvasCoursePath,
  externalToolsPath,
  assessmentsPath,
  finishedPath,
]

class App extends React.Component {
  static propTypes = {
    assessmentGroups: PropTypes.array.isRequired,
    canvasAssignments: PropTypes.array.isRequired,
    canvasCoursenum: PropTypes.string.isRequired,
    canvasExternalTools: PropTypes.object.isRequired,
    canvasTokenVerified: PropTypes.bool.isRequired,
    canvasUrl: PropTypes.string.isRequired,
    coursenum: PropTypes.string.isRequired,
    createCanvasAssignment: PropTypes.func.isRequired,
    createCourseLink: PropTypes.func.isRequired,
    deleteCanvasAssignment: PropTypes.func.isRequired,
    getAssessmentGroups: PropTypes.func.isRequired,
    getCanvasAssignments: PropTypes.func.isRequired,
    getCanvasCourses: PropTypes.func.isRequired,
    getCanvasExternalTools: PropTypes.func.isRequired,
    getCanvasSettings: PropTypes.func.isRequired,
    setCanvasUrl: PropTypes.func.isRequired,
    updateCanvasCoursenum: PropTypes.func.isRequired,
    updateCanvasUrl: PropTypes.func.isRequired,
    verifyCanvasToken: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props)
    const pathParts = window.location.pathname.split('/')
    const stepPath = pathParts[4]
    const step = Math.max(stepPaths.indexOf(stepPath), 0)
    this.basepath = pathParts.slice(0, 4).join('/')
    this.state = {
      error: null,
      step,
      submitting: false,
    }
  }

  _changeStep = step => {
    if (this._canAccessStep(step)) {
      this.setState({ error: null, step })
      const nextPath = path.join(this.basepath, stepPaths[step])
      navigate(nextPath)
    }
  }

  _canAccessStep = step => {
    let canAccess = true
    /* eslint-disable no-fallthrough */
    switch (step) {
      case stepPaths.indexOf(finishedPath):
      case stepPaths.indexOf(assessmentsPath):
        canAccess = canAccess && !!this.props.canvasExternalTools.course_link
      case stepPaths.indexOf(externalToolsPath):
        canAccess = canAccess && !!this.props.canvasCoursenum
      case stepPaths.indexOf(canvasCoursePath):
        canAccess = canAccess && !!this.props.canvasTokenVerified
      case stepPaths.indexOf(authorizationPath):
        canAccess = canAccess && !!this.props.canvasUrl
    }
    /* eslint-enable no-fallthrough */
    return canAccess
  }

  _saveCanvasUrl = async canvasUrl => {
    try {
      this.setState({ submitting: true })
      await this.props.updateCanvasUrl(canvasUrl)
      this._changeStep(this.state.step + 1)
    } catch (error) {
      this.setState({ error })
    } finally {
      this.setState({ submitting: false })
    }
  }

  _onVerifyToken = () => {
    this._changeStep(this.state.step + 1)
  }

  _saveCanvasCourse = async canvasCoursenum => {
    try {
      this.setState({ submitting: true })
      await this.props.updateCanvasCoursenum(canvasCoursenum)
      this._changeStep(this.state.step + 1)
    } catch (error) {
      this.setState({ error })
    } finally {
      this.setState({ submitting: false })
    }
  }

  _createExternalTools = async () => {
    const { course_link: courseLink } = this.props.canvasExternalTools
    if (!courseLink) await this.props.createCourseLink()
  }

  componentDidMount() {
    this.props.getCanvasSettings()
  }

  render() {
    return (
      <div>
        <StepWizard
          activeStep={this.state.step}
          numberOfSteps={5}
          onStepChanged={this._changeStep}
        />
        <div>
          {this.state.error ? (
            <div className="maxw-600px mx-auto text-center">
              <Alert
                bootstrapClass="danger"
                message={this.state.error}
                onDismiss={() => this.setState({ error: null })}
              />
            </div>
          ) : null}
          <Router basepath={this.basepath}>
            <CanvasUrl
              default
              path={canvasUrlPath}
              canvasUrl={this.props.canvasUrl}
              onChange={canvasUrl => this.props.setCanvasUrl(canvasUrl)}
              onSubmit={this._saveCanvasUrl}
              submitting={this.state.submitting}
            />
            <Authorization
              path={`${authorizationPath}/*`}
              coursenum={this.props.coursenum}
              onVerify={this._onVerifyToken}
              verify={this.props.verifyCanvasToken}
            />
            <CanvasCourse
              path={canvasCoursePath}
              canvasCoursenum={this.props.canvasCoursenum}
              getCanvasCourses={this.props.getCanvasCourses}
              onSubmit={this._saveCanvasCourse}
              submitting={this.state.submitting}
            />
            <CanvasExternalTools
              path={externalToolsPath}
              onCreateTools={this._createExternalTools}
              onFinish={() => this._changeStep(this.state.step + 1)}
              refresh={this.props.getCanvasExternalTools}
              tools={this.props.canvasExternalTools}
            />
            <CanvasAssessments
              path={assessmentsPath}
              assessmentGroups={this.props.assessmentGroups}
              canvasAssignments={this.props.canvasAssignments}
              createCanvasAssignment={this.props.createCanvasAssignment}
              deleteCanvasAssignment={this.props.deleteCanvasAssignment}
              getAssessmentGroups={this.props.getAssessmentGroups}
              getCanvasAssignments={this.props.getCanvasAssignments}
              onFinish={() => this._changeStep(this.state.step + 1)}
            />
            <Finished path={finishedPath} coursenum={this.props.coursenum} />
          </Router>
        </div>
      </div>
    )
  }
}

const mapStateToProps = state => ({ ...state })
const mapDispatchToProps = dispatch => ({
  createCanvasAssignment: assessmentPk =>
    dispatch(actions.createCanvasAssignment(assessmentPk)),
  createCourseLink: () => dispatch(actions.createCourseLink()),
  deleteCanvasAssignment: assessmentPk =>
    dispatch(actions.deleteCanvasAssignment(assessmentPk)),
  getAssessmentGroups: () => dispatch(actions.getAssessmentGroups()),
  getCanvasAssignments: () => dispatch(actions.getCanvasAssignments()),
  getCanvasCourses: () => dispatch(actions.getCanvasCourses()),
  getCanvasExternalTools: () => dispatch(actions.getCanvasExternalTools()),
  getCanvasSettings: () => dispatch(actions.getCanvasSettings()),
  setCanvasUrl: url => dispatch(actions.setCanvasUrl(url)),
  updateCanvasUrl: url => dispatch(actions.updateCanvasUrl(url)),
  updateCanvasCoursenum: coursenum =>
    dispatch(actions.updateCanvasCoursenum(coursenum)),
  verifyCanvasToken: () => dispatch(actions.verifyCanvasToken()),
})

export default connect(mapStateToProps, mapDispatchToProps)(App)
